import { useState } from "react";
import type { PatchStatus } from "../../api/types";
import { PATCH_STATUSES } from "../../api/types";
import { useDevicePatches } from "../../hooks/queries";
import {
  PatchStatusBadge,
  SeverityBadge,
} from "../../components/domain/Badges";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import { Select } from "../../components/ui/Input";
import { Pagination } from "../../components/ui/Pagination";
import { TableSkeleton } from "../../components/ui/Skeleton";
import { EmptyState, ErrorState } from "../../components/ui/States";
import { Table, TBody, TD, TH, THead, TR } from "../../components/ui/Table";
import { Badge } from "../../components/ui/Badge";
import { formatDate } from "../../lib/format";

const statusFilterOptions = [
  { value: "", label: "Tümü" },
  ...PATCH_STATUSES.map((status) => ({
    value: status,
    label: status === "installed" ? "Yüklü" : status === "pending" ? "Bekliyor" : status === "hidden" ? "Gizli" : "Başarısız",
  })),
];

export function DevicePatchesTab({ deviceId }: { deviceId: string }) {
  const [status, setStatus] = useState<PatchStatus | "">("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const query = useDevicePatches(deviceId, {
    status: status || undefined,
    page,
    perPage,
  });

  return (
    <Card>
      <CardHeader className="flex-row items-end justify-between gap-4">
        <div>
          <CardTitle>Patch kayıtları</CardTitle>
          <CardDescription>
            Agent'ın bildirdiği güncelleme/hotfix durumu
          </CardDescription>
        </div>
        <div className="w-48">
          <Select
            label="Durum filtresi"
            options={statusFilterOptions}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as PatchStatus | "");
              setPage(1);
            }}
          />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {query.isPending ? (
          <TableSkeleton rows={6} columns={6} />
        ) : query.isError ? (
          <ErrorState
            message="Patch kayıtları yüklenemedi."
            onRetry={() => void query.refetch()}
          />
        ) : (query.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="Patch kaydı yok"
            description="Bu cihaz için seçilen ölçüte uyan güncelleme bulunamadı."
          />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Kimlik</TH>
                <TH>Başlık</TH>
                <TH>Kaynak</TH>
                <TH>Önem</TH>
                <TH>Durum</TH>
                <TH>Yayın</TH>
                <TH>Kurulum</TH>
                <TH>Yeniden başlatma</TH>
              </TR>
            </THead>
            <TBody>
              {query.data?.items.map((record) => (
                <TR key={record.id}>
                  <TD className="font-mono text-xs">{record.identifier}</TD>
                  <TD className="max-w-xs truncate" title={record.title}>
                    {record.title ?? "—"}
                  </TD>
                  <TD className="text-muted-foreground text-xs">{record.source}</TD>
                  <TD>
                    <SeverityBadge severity={record.severity} />
                  </TD>
                  <TD>
                    <PatchStatusBadge status={record.status} />
                  </TD>
                  <TD className="text-muted-foreground whitespace-nowrap">
                    {formatDate(record.publishedAt)}
                  </TD>
                  <TD className="text-muted-foreground whitespace-nowrap">
                    {formatDate(record.installedAt)}
                  </TD>
                  <TD>
                    {record.rebootRequired ? (
                      <Badge tone="warning">Gerekli</Badge>
                    ) : (
                      <span className="text-muted-foreground text-xs">Hayır</span>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}

        {query.data ? (
          <Pagination
            page={query.data.pageMeta.page}
            perPage={query.data.pageMeta.perPage}
            total={query.data.pageMeta.total}
            onPageChange={setPage}
            onPerPageChange={(next) => {
              setPerPage(next);
              setPage(1);
            }}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}